import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Building2, 
  MapPin, 
  Phone, 
  ExternalLink, 
  Clock, 
  Moon, 
  Bed, 
  Copy, 
  Check, 
  Edit3, 
  Eye, 
  X, 
  Save, 
  CheckCircle2,
  FileText
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import vouchersData from '../../../../../../../data/vouchers/vouchersData.json';
import LocationDropdown from '../../../../../../../components/forms/LocationDropdown';

const mealPlans = vouchersData?.mealPlans || [
  { label: "EP (Room Only)", value: "EP" },
  { label: "CP (Breakfast)", value: "CP" },
  { label: "CPAI (Breakfast Included)", value: "CPAI" },
  { label: "MAP (Bf + Dinner)", value: "MAP" },
  { label: "MAPAI (Bf + Dinner Included)", value: "MAPAI" },
  { label: "AP (All Meals)", value: "AP" }
];

const formatDisplayDateLong = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const HotelDetailsModal = ({
  isOpen,
  onClose,
  hotel,
  hotelIndex,
  onSaveHotel,
  initialEditMode = false
}) => {
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [copiedCode, setCopiedCode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(64);

  // Form draft state
  const [formData, setFormData] = useState({
    hotel_name: '',
    city: '',
    destination: '',
    location_id: '',
    hotel_address: '',
    map_link: '',
    hotel_phone: '',
    room_category: '',
    meal_plan: 'CP',
    check_in_date: '',
    check_in_time: '14:00',
    check_out_date: '',
    check_out_time: '11:00',
    no_of_rooms: 1,
    no_of_nights: 1,
    confirmation_code: '',
    remarks: '',
    added_by: 'agent'
  });

  // Dynamically measure portal header and prevent background scrolling
  useEffect(() => {
    if (!isOpen) return;

    const updateHeaderHeight = () => {
      const headerEl = document.querySelector('header');
      if (headerEl) {
        const rect = headerEl.getBoundingClientRect();
        if (rect.height > 0) {
          setHeaderHeight(rect.height);
          document.documentElement.style.setProperty('--portal-header-height', `${rect.height}px`);
        }
      }
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    const prevBodyOverflow = document.body.style.overflow;
    const mainEl = document.querySelector('main');
    const prevMainOverflow = mainEl ? mainEl.style.overflow : '';

    document.body.style.overflow = 'hidden';
    if (mainEl) mainEl.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevBodyOverflow;
      if (mainEl) mainEl.style.overflow = prevMainOverflow;
    };
  }, [isOpen, onClose]);

  const resetFormFromHotel = (h) => {
    if (!h) return;
    setFormData({
      hotel_name: h.hotel_name || h.name || '',
      city: h.city || h.destination || '',
      destination: h.destination || h.city || '',
      location_id: h.location_id || '',
      hotel_address: h.hotel_address || h.address || '',
      map_link: h.map_link || '',
      hotel_phone: h.hotel_phone || h.phone || '',
      room_category: h.room_category || h.room_type || '',
      meal_plan: h.meal_plan || 'CP',
      check_in_date: h.check_in_date || '',
      check_in_time: h.check_in_time || '14:00',
      check_out_date: h.check_out_date || '',
      check_out_time: h.check_out_time || '11:00',
      no_of_rooms: h.no_of_rooms || h.rooms_count || 1,
      no_of_nights: h.no_of_nights || h.nights_count || 1,
      confirmation_code: h.confirmation_code || h.confirmation_number || '',
      remarks: h.remarks || h.inclusions || h.special_requests || '',
      added_by: h.added_by || 'agent'
    });
  };

  useEffect(() => {
    if (hotel) {
      resetFormFromHotel(hotel);
      setIsEditing(initialEditMode);
    }
  }, [hotel, initialEditMode, isOpen]);

  if (!isOpen || !hotel) return null;

  const handleCopyCode = (code) => {
    if (!code) return;
    navigator.clipboard?.writeText(code);
    setCopiedCode(true);
    toast.success('Confirmation code copied!');
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCancelEdit = () => {
    resetFormFromHotel(hotel);
    setIsEditing(false);
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!formData.hotel_name.trim()) {
      toast.error('Please enter the hotel name.');
      return;
    }

    setSaving(true);
    try {
      const updatedHotel = {
        ...hotel,
        ...formData,
        name: formData.hotel_name,
        city: formData.city,
        destination: formData.city,
        location_id: formData.location_id || hotel.location_id || '',
        address: formData.hotel_address,
        phone: formData.hotel_phone,
        confirmation_number: formData.confirmation_code,
        rooms_count: Number(formData.no_of_rooms) || 1,
        nights_count: Number(formData.no_of_nights) || 1
      };

      await onSaveHotel(hotelIndex, updatedHotel);
      setIsEditing(false);
      toast.success('Hotel details updated successfully!');
    } catch (err) {
      console.error('Failed to update hotel:', err);
      toast.error('Failed to update hotel details.');
    } finally {
      setSaving(false);
    }
  };

  const getMealPlanLabel = (planCode) => {
    const match = mealPlans.find(p => p.value === planCode);
    return match ? match.label : (planCode || 'Room Only');
  };

  const mapUrl = formData.map_link || (formData.hotel_name
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${formData.hotel_name} ${formData.city || ''}`)}`
    : null);

  const modalContent = (
    <div 
      className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-center p-3 sm:p-5 bg-slate-900/60 backdrop-blur-sm animate-fade-in"
      style={{ top: `${headerHeight}px`, height: `calc(100vh - ${headerHeight}px)` }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div 
        className="bg-white rounded-3xl shadow-2xl border border-slate-200/90 w-full max-w-2xl flex flex-col overflow-hidden animate-scale-in"
        style={{ maxHeight: `calc(100% - 1.5rem)` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Top Bar */}
        <div className="px-5 sm:px-6 py-3.5 border-b border-slate-100 flex items-center justify-between gap-3 bg-white shrink-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-2xl bg-teal-50 border border-teal-100/80 flex items-center justify-center text-[#067978] shrink-0 shadow-2xs">
              <Building2 size={20} />
            </div>
            <div className="min-w-0 flex-1">
              {!isEditing ? (
                <>
                  <h3 className="text-base sm:text-lg font-semibold text-slate-800 truncate">
                    {formData.hotel_name || 'Hotel Details'}
                  </h3>
                  <p className="text-xs text-slate-500 font-normal truncate">
                    {formData.city ? `${formData.city} · Stay Accommodation` : 'Stay Accommodation Details'}
                  </p>
                </>
              ) : (
                <div className="space-y-0.5">
                  <input
                    type="text"
                    required
                    value={formData.hotel_name}
                    onChange={(e) => handleInputChange('hotel_name', e.target.value)}
                    placeholder="Hotel Name *"
                    className="w-full text-sm sm:text-base font-medium text-slate-800 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1 focus:outline-none focus:border-[#067978] focus:bg-white transition-all"
                  />
                  <div className="text-[11px] text-slate-400 font-normal px-0.5">
                    Assigned Destination: <span className="font-medium text-slate-600">{formData.city || 'Not selected'}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* View / Edit Mode Toggle Pills */}
            <div className="inline-flex items-center bg-slate-100 p-0.5 rounded-xl border border-slate-200/60">
              <button
                type="button"
                onClick={() => {
                  if (isEditing) handleCancelEdit();
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                  !isEditing
                    ? 'bg-white text-[#067978] shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Eye size={13} />
                <span className="hidden sm:inline">View</span>
              </button>
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                  isEditing
                    ? 'bg-white text-[#067978] shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Edit3 size={13} />
                <span className="hidden sm:inline">Edit</span>
              </button>
            </div>

            {/* Close Button */}
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Modal Scrollable Body: PRESERVES EXACT SAME CARDS & SPACING IN BOTH VIEW & EDIT MODE */}
        <div className="p-5 sm:p-6 overflow-y-auto space-y-4 no-scrollbar flex-1 overscroll-contain">
          {/* 1. Stay Timeline Hero Card */}
          <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-teal-50/90 via-emerald-50/40 to-slate-50 border border-teal-200/70 shadow-2xs">
            <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-4 text-center sm:text-left">
              {/* Check-In */}
              <div>
                <span className="text-[10px] font-medium uppercase tracking-wide text-teal-700 bg-teal-100/60 px-2 py-0.5 rounded-md">
                  Check-In
                </span>
                {!isEditing ? (
                  <>
                    <div className="text-sm sm:text-base font-medium text-slate-800 mt-1.5">
                      {formatDisplayDateLong(formData.check_in_date)}
                    </div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5 flex items-center justify-center sm:justify-start gap-1">
                      <Clock size={12} className="text-teal-600" />
                      <span>{formData.check_in_time || '14:00'} (24-hour)</span>
                    </div>
                  </>
                ) : (
                  <div className="mt-2 space-y-1.5">
                    <input
                      type="date"
                      value={formData.check_in_date}
                      onChange={(e) => handleInputChange('check_in_date', e.target.value)}
                      className="w-full text-xs font-normal bg-white border border-teal-200 rounded-xl px-2.5 py-1.5 text-slate-800 focus:outline-none focus:border-[#067978]"
                    />
                    <div className="flex items-center gap-1.5">
                      <Clock size={12} className="text-teal-600 shrink-0" />
                      <input
                        type="text"
                        value={formData.check_in_time}
                        onChange={(e) => handleInputChange('check_in_time', e.target.value)}
                        placeholder="14:00 (24h)"
                        className="w-full text-xs font-mono font-normal bg-white border border-teal-200 rounded-lg px-2 py-1 text-slate-800 focus:outline-none focus:border-[#067978]"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Duration Pill in Middle */}
              <div className="flex flex-col items-center justify-center py-2 sm:py-0 border-y sm:border-y-0 sm:border-x border-teal-200/50 sm:px-2">
                <div className="w-8 h-8 rounded-full bg-white text-[#067978] shadow-2xs border border-teal-100 flex items-center justify-center mb-1">
                  <Moon size={15} />
                </div>
                {!isEditing ? (
                  <>
                    <span className="text-xs font-medium text-slate-800">
                      {formData.no_of_nights || 1} {Number(formData.no_of_nights) === 1 ? 'Night' : 'Nights'}
                    </span>
                    <span className="text-[11px] text-slate-500 font-normal">
                      {formData.no_of_rooms || 1} {Number(formData.no_of_rooms) === 1 ? 'Room' : 'Rooms'}
                    </span>
                  </>
                ) : (
                  <div className="w-full max-w-[130px] space-y-1.5">
                    <div className="flex items-center justify-between gap-1 bg-white/80 border border-teal-200 rounded-lg px-2 py-0.5">
                      <span className="text-[10px] text-slate-500 font-normal">Nights:</span>
                      <input
                        type="number"
                        min="1"
                        value={formData.no_of_nights}
                        onChange={(e) => handleInputChange('no_of_nights', e.target.value)}
                        className="w-12 text-xs font-medium text-slate-800 text-right focus:outline-none"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-1 bg-white/80 border border-teal-200 rounded-lg px-2 py-0.5">
                      <span className="text-[10px] text-slate-500 font-normal">Rooms:</span>
                      <input
                        type="number"
                        min="1"
                        value={formData.no_of_rooms}
                        onChange={(e) => handleInputChange('no_of_rooms', e.target.value)}
                        className="w-12 text-xs font-medium text-slate-800 text-right focus:outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Check-Out */}
              <div className="sm:text-right">
                <span className="text-[10px] font-medium uppercase tracking-wide text-slate-600 bg-slate-200/70 px-2 py-0.5 rounded-md">
                  Check-Out
                </span>
                {!isEditing ? (
                  <>
                    <div className="text-sm sm:text-base font-medium text-slate-800 mt-1.5">
                      {formatDisplayDateLong(formData.check_out_date)}
                    </div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5 flex items-center justify-center sm:justify-end gap-1">
                      <Clock size={12} className="text-slate-400" />
                      <span>{formData.check_out_time || '11:00'} (24-hour)</span>
                    </div>
                  </>
                ) : (
                  <div className="mt-2 space-y-1.5">
                    <input
                      type="date"
                      value={formData.check_out_date}
                      onChange={(e) => handleInputChange('check_out_date', e.target.value)}
                      className="w-full text-xs font-normal bg-white border border-slate-300 rounded-xl px-2.5 py-1.5 text-slate-800 focus:outline-none focus:border-[#067978]"
                    />
                    <div className="flex items-center justify-end gap-1.5">
                      <Clock size={12} className="text-slate-400 shrink-0" />
                      <input
                        type="text"
                        value={formData.check_out_time}
                        onChange={(e) => handleInputChange('check_out_time', e.target.value)}
                        placeholder="11:00 (24h)"
                        className="w-full text-xs font-mono font-normal bg-white border border-slate-300 rounded-lg px-2 py-1 text-slate-800 focus:outline-none focus:border-[#067978]"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 2. Details Grid (Room, Meal Plan, Confirmation, Location) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Room & Meal Plan Card */}
            <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-2xs space-y-3">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-100 text-xs font-medium uppercase tracking-wide text-slate-500">
                <Bed size={14} className="text-[#067978]" />
                <span>Room &amp; Meal Plan</span>
              </div>

              <div>
                <span className="text-[11px] text-slate-400 font-normal block">Room Category</span>
                {!isEditing ? (
                  <span className="text-xs sm:text-sm font-medium text-slate-700 mt-0.5 block">
                    {formData.room_category || 'Standard Room'}
                  </span>
                ) : (
                  <input
                    type="text"
                    value={formData.room_category}
                    onChange={(e) => handleInputChange('room_category', e.target.value)}
                    placeholder="e.g. Deluxe Room"
                    className="w-full text-xs sm:text-sm font-normal bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-slate-800 mt-1 focus:outline-none focus:border-[#067978] focus:bg-white"
                  />
                )}
              </div>

              {/* Meal Plan: Regular text without pill/badge treatment */}
              <div>
                <span className="text-[11px] text-slate-400 font-normal block">Meal Plan</span>
                {!isEditing ? (
                  <span className="text-xs sm:text-sm font-normal text-slate-700 mt-0.5 block">
                    {getMealPlanLabel(formData.meal_plan)}
                  </span>
                ) : (
                  <select
                    value={formData.meal_plan}
                    onChange={(e) => handleInputChange('meal_plan', e.target.value)}
                    className="w-full text-xs sm:text-sm font-normal bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-slate-800 mt-1 focus:outline-none focus:border-[#067978] focus:bg-white cursor-pointer"
                  >
                    {mealPlans.map((mp) => (
                      <option key={mp.value} value={mp.value}>
                        {mp.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Booking & Confirmation Reference Card */}
            <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-2xs space-y-3">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-100 text-xs font-medium uppercase tracking-wide text-slate-500">
                <FileText size={14} className="text-[#067978]" />
                <span>Booking Confirmation</span>
              </div>

              <div>
                <span className="text-[11px] text-slate-400 font-normal block">Confirmation Code</span>
                {!isEditing ? (
                  formData.confirmation_code ? (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs sm:text-sm font-mono font-medium text-slate-700 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-md">
                        {formData.confirmation_code}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCopyCode(formData.confirmation_code)}
                        className="p-1 text-slate-400 hover:text-[#067978] transition-colors cursor-pointer"
                        title="Copy Code"
                      >
                        {copiedCode ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400 italic">Not specified</span>
                  )
                ) : (
                  <input
                    type="text"
                    value={formData.confirmation_code}
                    onChange={(e) => handleInputChange('confirmation_code', e.target.value)}
                    placeholder="e.g. CONF-882190"
                    className="w-full text-xs font-mono font-normal text-slate-800 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 mt-1 focus:outline-none focus:border-[#067978] focus:bg-white"
                  />
                )}
              </div>

              <div>
                <span className="text-[11px] text-slate-400 font-normal block">Attribution</span>
                <span className="text-xs font-normal text-slate-600 capitalize mt-0.5 block">
                  Added by {formData.added_by === 'dmc' ? 'DMC' : 'Agent'}
                </span>
              </div>
            </div>

            {/* Destination, Location & Contact Card (Span 2) */}
            <div className="sm:col-span-2 p-4 rounded-2xl bg-white border border-slate-200/80 shadow-2xs space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                  <MapPin size={14} className="text-[#067978]" />
                  <span>Destination &amp; Address</span>
                </div>

                {!isEditing && mapUrl && (
                  <a
                    href={mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-normal text-[#067978] hover:text-[#056564] hover:underline"
                  >
                    <span>Open in Google Maps</span>
                    <ExternalLink size={12} />
                  </a>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Assigned Destination using LocationDropdown */}
                <div>
                  <span className="text-[11px] text-slate-400 font-normal block mb-1">
                    Assign Destination *
                  </span>
                  {!isEditing ? (
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <MapPin size={13} className="text-[#067978] shrink-0" />
                      <span className="text-xs sm:text-sm font-medium text-slate-700">
                        {formData.city || formData.destination || 'Not assigned'}
                      </span>
                    </div>
                  ) : (
                    <LocationDropdown
                      value={formData.location_id || formData.city}
                      valueKey={formData.location_id ? '_id' : 'name'}
                      onChange={(val, loc) => {
                        const cityName = loc?.name || loc?.city || loc?.location_name || val;
                        const locId = loc?._id || loc?.location_id || val;
                        setFormData(prev => ({
                          ...prev,
                          city: cityName,
                          destination: cityName,
                          location_id: locId
                        }));
                      }}
                      placeholder="Select Destination"
                      buttonClassName="h-9 px-3 text-xs font-normal"
                    />
                  )}
                </div>

                {/* Hotel Address / Area */}
                <div>
                  <span className="text-[11px] text-slate-400 font-normal block mb-1">
                    Hotel Address / Area
                  </span>
                  {!isEditing ? (
                    <p className="text-xs text-slate-700 font-normal mt-0.5 leading-relaxed">
                      {formData.hotel_address || formData.city || 'No specific address provided.'}
                    </p>
                  ) : (
                    <input
                      type="text"
                      value={formData.hotel_address}
                      onChange={(e) => handleInputChange('hotel_address', e.target.value)}
                      placeholder="e.g. Candolim Beach Road, North Goa"
                      className="w-full text-xs font-normal bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-[#067978] focus:bg-white"
                    />
                  )}
                </div>

                {/* Contact Phone */}
                <div>
                  <span className="text-[11px] text-slate-400 font-normal block mb-1">
                    Contact Phone
                  </span>
                  {!isEditing ? (
                    formData.hotel_phone ? (
                      <a
                        href={`tel:${formData.hotel_phone}`}
                        className="inline-flex items-center gap-1.5 text-xs font-normal text-[#067978] mt-1 hover:underline"
                      >
                        <Phone size={12} />
                        <span>{formData.hotel_phone}</span>
                      </a>
                    ) : (
                      <span className="text-xs text-slate-400 italic mt-0.5 block">No phone number attached</span>
                    )
                  ) : (
                    <input
                      type="text"
                      value={formData.hotel_phone}
                      onChange={(e) => handleInputChange('hotel_phone', e.target.value)}
                      placeholder="+91 98201 12345"
                      className="w-full text-xs font-normal bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-[#067978] focus:bg-white"
                    />
                  )}
                </div>

                {/* Google Maps Link (only in Edit mode) */}
                {isEditing && (
                  <div>
                    <span className="text-[11px] text-slate-400 font-normal block mb-1">
                      Google Maps Link
                    </span>
                    <input
                      type="url"
                      value={formData.map_link}
                      onChange={(e) => handleInputChange('map_link', e.target.value)}
                      placeholder="https://maps.google.com/..."
                      className="w-full text-xs font-normal bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-[#067978] focus:bg-white"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Remarks & Inclusions */}
            {((!isEditing && formData.remarks) || isEditing) && (
              <div className="sm:col-span-2 p-4 rounded-2xl bg-amber-50/40 border border-amber-200/60 shadow-2xs space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-amber-800">
                  <CheckCircle2 size={13} className="text-amber-600" />
                  <span>Inclusions &amp; Special Remarks</span>
                </div>
                {!isEditing ? (
                  <p className="text-xs text-slate-700 leading-relaxed font-normal">
                    {formData.remarks}
                  </p>
                ) : (
                  <textarea
                    rows={2}
                    value={formData.remarks}
                    onChange={(e) => handleInputChange('remarks', e.target.value)}
                    placeholder="e.g. Complimentary breakfast and airport shuttle included"
                    className="w-full text-xs font-normal bg-white border border-amber-200 rounded-xl px-3 py-1.5 text-slate-800 mt-1 focus:outline-none focus:border-[#067978]"
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Modal Pinned Actions Footer */}
        <div className="px-5 sm:px-6 py-3.5 border-t border-slate-100 flex items-center justify-end gap-3 bg-white shrink-0">
          {!isEditing ? (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-[#067978] hover:bg-[#056564] text-white text-xs sm:text-sm font-medium rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer active:scale-95"
            >
              <Edit3 size={14} />
              <span>Edit Hotel Details</span>
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs sm:text-sm font-medium rounded-xl transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 bg-[#067978] hover:bg-[#056564] text-white text-xs sm:text-sm font-medium rounded-xl shadow-xs transition-all cursor-pointer active:scale-95 disabled:opacity-50 flex items-center gap-1.5"
              >
                <Save size={14} />
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
};

export default HotelDetailsModal;
